import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Text, VisuallyHidden } from "@chakra-ui/react";
import { AnimatePresence, useInView } from "framer-motion";
import { MotionBox } from "ui/chakra-motion";

const warehouseVendors = ["Snowflake", "Databricks", "BigQuery", "Redshift"];
const genericWarehouseText = "your data warehouse";

const rotatingTextVariants = {
  initial: { opacity: 0, y: -20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      opacity: {
        duration: 0.1,
        delay: 0.3,
      },
      y: {
        delay: 0.3,
        type: "spring",
        damping: 30,
        stiffness: 700,
      },
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      opacity: {
        duration: 0.1,
      },
      y: {
        duration: 0.25,
      },
    },
  },
};

/** Rotates through the warehouse names [Snowflake, Databricks, BigQuery, Redshift] every 2.5 seconds */
export function WarehouseNameRotation({
  hasGenericWarehouse,
  isCentered,
}: {
  hasGenericWarehouse?: boolean;
  isCentered?: boolean;
}) {
  const scope = useRef<HTMLDivElement>(null);
  const isInView = useInView(scope, { once: true, amount: "some" });

  const warehouses = useMemo(
    () => (hasGenericWarehouse ? ["generic", ...warehouseVendors] : warehouseVendors),
    [hasGenericWarehouse],
  );
  const [activeWarehouse, setActiveWarehouse] = useState(warehouses[0]);

  // Rotate through the warehouses every 2.5 seconds
  useEffect(() => {
    if (isInView) {
      const interval = setInterval(() => {
        setActiveWarehouse((previous) => {
          const currentIndex = warehouses.indexOf(previous);
          const nextIndex = currentIndex + 1 === warehouses.length ? 0 : currentIndex + 1;
          return warehouses[nextIndex];
        });
      }, 2500);

      return () => {
        clearInterval(interval);
      };
    }
  }, [isInView, warehouses]);

  return (
    <Box ref={scope} as="span" display="inline-block" pos="relative">
      {/* Keep the longest word with opacity 0 to avoid layout shift */}
      <Text aria-hidden as="span" opacity={0}>
        {hasGenericWarehouse ? genericWarehouseText : "Databricks"}
      </Text>

      {/* Keep all warehouse names available for a11y */}
      <VisuallyHidden>
        {hasGenericWarehouse
          ? "Snowflake, Databricks, BigQuery, Redshift, or your data warehouse"
          : "Snowflake, Databricks, BigQuery, and Redshift"}
      </VisuallyHidden>

      {warehouses.map((warehouse) => (
        <AnimatePresence key={warehouse} initial={false}>
          {activeWarehouse === warehouse && (
            <MotionBox
              aria-hidden
              pos="absolute"
              inset={0}
              right={isCentered ? undefined : "auto"}
              h="100%"
              variants={rotatingTextVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <Text as="span" color={`tools.${warehouse.toLowerCase()}`}>
                {warehouse === "generic" ? genericWarehouseText : warehouse}
              </Text>
            </MotionBox>
          )}
        </AnimatePresence>
      ))}
    </Box>
  );
}
